import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, ArrowLeft, Mail, Phone, Globe, Linkedin, MapPin, Edit2, Save, X,
  StickyNote, MessageSquare, PhoneCall, Calendar, Target, AlertTriangle,
  Trash2, CheckCircle2, Circle, Plus, Clock, Send, FileSignature, Sparkles,
  Brain, RefreshCw, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CATEGORIES, STATUSES, OUTREACH_STAGES, STATUS_TONE, STAGE_TONE, formatRel, initials } from "./constants";
import OutreachModal from "./OutreachModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTIVITY_ICON = {
  NOTE: StickyNote,
  EMAIL: Mail,
  CALL: PhoneCall,
  FOLLOW_UP: Calendar,
  DEMO: Target,
  STATUS_CHANGE: CheckCircle2,
  STAGE_CHANGE: Send,
  AI_MESSAGE_GENERATED: FileSignature,
  TASK_CREATED: Plus,
  TASK_COMPLETED: CheckCircle2,
  IMPORT: Globe,
  CONTACT_FORM: MessageSquare,
};

export default function AdminCrmContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [showOutreach, setShowOutreach] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [c, a, t] = await Promise.all([
        axios.get(`${API}/admin/crm/contacts/${id}`),
        axios.get(`${API}/admin/crm/contacts/${id}/activities`),
        axios.get(`${API}/admin/crm/tasks`, { params: { contact_id: id } }),
      ]);
      setContact(c.data);
      setActivities(a.data.items);
      setTasks(t.data.items);
      setDraft(c.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load contact");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveContact = async () => {
    setSaving(true);
    try {
      const payload = {
        name: draft.name,
        email: draft.email || null,
        phone: draft.phone || null,
        title: draft.title || null,
        organization: draft.organization || null,
        city: draft.city || null,
        country: draft.country || null,
        linkedin_url: draft.linkedin_url || null,
        website: draft.website || null,
        category: draft.category,
        status: draft.status,
        outreach_stage: draft.outreach_stage,
        lead_score: Number(draft.lead_score) || 0,
        priority_score: Number(draft.priority_score) || 0,
        notes_summary: draft.notes_summary || null,
        next_follow_up_at: draft.next_follow_up_at || null,
      };
      const { data } = await axios.put(`${API}/admin/crm/contacts/${id}`, payload);
      setContact({ ...contact, ...data });
      setEditing(false);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteContact = async () => {
    if (!window.confirm(`Delete ${contact.name}? This also deletes their activities and tasks.`)) return;
    try {
      await axios.delete(`${API}/admin/crm/contacts/${id}`);
      toast.success("Contact deleted");
      navigate("/admin/crm/contacts");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const quickStatus = async (newStatus) => {
    try {
      await axios.put(`${API}/admin/crm/contacts/${id}`, { status: newStatus });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };
  const quickStage = async (newStage) => {
    try {
      await axios.put(`${API}/admin/crm/contacts/${id}`, { outreach_stage: newStage });
      toast.success("Stage updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;
  }
  if (error || !contact) {
    return <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{error || "Not found"}</div>;
  }

  return (
    <div className="space-y-5" data-testid="crm-contact-detail">
      {/* Header */}
      <div>
        <Link to="/admin/crm/contacts" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="w-4 h-4" /> All contacts
        </Link>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-wrap gap-6 items-start">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-indigo-500/20 ring-1 ring-cyan-200 flex items-center justify-center text-xl font-bold text-cyan-800">
          {initials(contact.name)}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] truncate">{contact.name}</h1>
          <p className="text-slate-600 text-sm truncate">{[contact.title, contact.organization].filter(Boolean).join(" · ") || "—"}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 ring-1 ring-slate-200">{contact.category}</span>
            <select
              value={contact.status}
              onChange={(e) => quickStatus(e.target.value)}
              data-testid="crm-quick-status"
              className={`text-xs px-2 py-0.5 rounded-md ring-1 cursor-pointer ${STATUS_TONE[contact.status] || ""}`}
            >
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <select
              value={contact.outreach_stage}
              onChange={(e) => quickStage(e.target.value)}
              data-testid="crm-quick-stage"
              className={`text-xs px-2 py-0.5 rounded-md ring-1 cursor-pointer ${STAGE_TONE[contact.outreach_stage] || ""}`}
            >
              {OUTREACH_STAGES.map((s) => <option key={s}>{s}</option>)}
            </select>
            {(contact.tags || []).map((t) => (
              <span key={t} className="text-xs px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200">#{t}</span>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
            {contact.email && <a href={`mailto:${contact.email}`} className="inline-flex items-center gap-1 hover:text-cyan-700"><Mail className="w-3 h-3" />{contact.email}</a>}
            {contact.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{contact.phone}</span>}
            {(contact.city || contact.country) && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[contact.city, contact.country].filter(Boolean).join(", ")}</span>}
            {contact.linkedin_url && <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-cyan-700"><Linkedin className="w-3 h-3" />LinkedIn</a>}
            {contact.website && <a href={contact.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-cyan-700"><Globe className="w-3 h-3" />Website</a>}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            size="sm"
            onClick={() => setShowOutreach(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="crm-outreach-open"
          >
            <Sparkles className="w-4 h-4 mr-1.5" /> AI Outreach
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEditing((v) => !v)} data-testid="crm-edit-toggle">
            <Edit2 className="w-4 h-4 mr-1.5" /> {editing ? "Cancel" : "Edit"}
          </Button>
          <Button variant="outline" size="sm" onClick={deleteContact} data-testid="crm-delete-btn" className="text-red-600 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-1.5" /> Delete
          </Button>
        </div>
      </div>

      {showOutreach && (
        <OutreachModal
          contact={contact}
          onClose={() => setShowOutreach(false)}
          onGenerated={load}
        />
      )}

      {/* Edit drawer */}
      {editing && (
        <EditPanel draft={draft} setDraft={setDraft} onSave={saveContact} saving={saving} onCancel={() => { setEditing(false); setDraft(contact); }} />
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Lead score" value={contact.lead_score ?? 0} />
        <Stat label="Priority" value={contact.priority_score ?? 0} />
        <Stat label="Activities" value={contact.activity_count ?? activities.length} />
        <Stat label="Open tasks" value={contact.open_tasks ?? tasks.filter((t) => !t.completed).length} />
      </div>

      {/* Two-col body */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <AiSummaryPanel contact={contact} reload={load} />
          <ActivityPanel contactId={id} activities={activities} reload={load} />
          <NotesPanel contact={contact} reload={load} />
        </div>
        <div className="space-y-5">
          <TasksPanel contactId={id} tasks={tasks} reload={load} />
          <RelationshipPanel contact={contact} />
        </div>
      </div>
    </div>
  );
}

function AiSummaryPanel({ contact, reload }) {
  const summary = contact.ai_summary;
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/crm/contacts/${contact.id}/ai-summary`);
      toast.success(summary ? "Summary refreshed" : "Summary generated");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="rounded-2xl border border-indigo-200 p-5 bg-gradient-to-br from-indigo-50 via-white to-cyan-50"
      data-testid="crm-ai-summary-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-800">Relationship intelligence</h3>
          {summary && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white text-slate-500 ring-1 ring-slate-200 font-mono">
              {summary.model || "ai"}
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={generate}
          disabled={busy}
          data-testid="crm-ai-summary-generate"
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : summary ? (
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          ) : (
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
          )}
          {summary ? "Refresh" : "Generate"}
        </Button>
      </div>

      {!summary ? (
        <p className="text-sm text-slate-500 leading-relaxed">
          Generate an AI-powered snapshot of this relationship — key signals, suggested next action, and any risk flags worth knowing before reaching out.
        </p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-800 leading-relaxed" data-testid="crm-ai-summary-text">
            {summary.summary}
          </p>

          {summary.signals?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Signals</div>
              <ul className="space-y-1">
                {summary.signals.map((s, i) => (
                  <li key={i} className="text-xs text-slate-700 flex items-start gap-2" data-testid={`crm-ai-summary-signal-${i}`}>
                    <span className="w-1 h-1 rounded-full bg-indigo-500 mt-1.5 flex-shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {summary.next_action && (
            <div className="bg-white rounded-md ring-1 ring-indigo-200 px-3 py-2 flex items-start gap-2" data-testid="crm-ai-summary-next-action">
              <ArrowRight className="w-3.5 h-3.5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-indigo-700">Next action</div>
                <div className="text-xs text-slate-800">{summary.next_action}</div>
              </div>
            </div>
          )}

          {summary.risk_flags?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 mb-1.5">Risk flags</div>
              <ul className="space-y-1">
                {summary.risk_flags.map((s, i) => (
                  <li key={i} className="text-xs text-amber-800 flex items-start gap-2" data-testid={`crm-ai-summary-risk-${i}`}>
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-[10px] text-slate-400 pt-1">
            Generated {formatRel(summary.generated_at)} · {summary.provider}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function EditPanel({ draft, setDraft, onSave, saving, onCancel }) {
  const set = (k, v) => setDraft((p) => ({ ...p, [k]: v }));
  const F = ({ label, k, type = "text", multiline, options }) => (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</span>
      {options ? (
        <select className="mt-1 w-full text-sm rounded-md border border-slate-300 px-3 py-2 bg-white" value={draft[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`crm-edit-${k}`}>
          {options.map((o) => <option key={o}>{o}</option>)}
        </select>
      ) : multiline ? (
        <textarea rows={3} className="mt-1 w-full text-sm rounded-md border border-slate-300 px-3 py-2" value={draft[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`crm-edit-${k}`} />
      ) : (
        <input type={type} className="mt-1 w-full text-sm rounded-md border border-slate-300 px-3 py-2" value={draft[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`crm-edit-${k}`} />
      )}
    </label>
  );
  return (
    <div className="bg-white border border-cyan-200 rounded-2xl p-5 space-y-3" data-testid="crm-edit-panel">
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Name" k="name" />
        <F label="Email" k="email" type="email" />
        <F label="Phone" k="phone" />
        <F label="Title" k="title" />
        <F label="Organization" k="organization" />
        <F label="Category" k="category" options={CATEGORIES} />
        <F label="Status" k="status" options={STATUSES} />
        <F label="Outreach stage" k="outreach_stage" options={OUTREACH_STAGES} />
        <F label="City" k="city" />
        <F label="Country" k="country" />
        <F label="LinkedIn URL" k="linkedin_url" />
        <F label="Website" k="website" />
        <F label="Lead score (0-100)" k="lead_score" type="number" />
        <F label="Priority (0-100)" k="priority_score" type="number" />
        <label className="block sm:col-span-2">
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Next follow-up</span>
          <input
            type="datetime-local"
            value={draft.next_follow_up_at ? new Date(draft.next_follow_up_at).toISOString().slice(0, 16) : ""}
            onChange={(e) => set("next_follow_up_at", e.target.value ? new Date(e.target.value).toISOString() : null)}
            className="mt-1 w-full text-sm rounded-md border border-slate-300 px-3 py-2"
            data-testid="crm-edit-next_follow_up_at"
          />
        </label>
        <div className="sm:col-span-2"><F label="Summary / notes" k="notes_summary" multiline /></div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onCancel}><X className="w-4 h-4 mr-1.5" /> Cancel</Button>
        <Button size="sm" onClick={onSave} disabled={saving} className="bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="crm-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Save changes
        </Button>
      </div>
    </div>
  );
}

function ActivityPanel({ contactId, activities, reload }) {
  const [type, setType] = useState("NOTE");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/crm/contacts/${contactId}/activities`, {
        type, body: body.trim(),
      });
      setBody("");
      toast.success("Logged");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <h3 className="text-sm font-semibold text-slate-800 mb-3">Activity</h3>
      <div className="bg-slate-50 rounded-xl p-3 mb-4">
        <div className="flex flex-wrap gap-2 mb-2">
          {["NOTE", "EMAIL", "CALL", "DEMO", "FOLLOW_UP"].map((t) => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`text-xs px-2 py-1 rounded-md ring-1 ${type === t ? "bg-cyan-700 text-white ring-cyan-700" : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"}`}
              data-testid={`crm-activity-type-${t}`}
            >{t.toLowerCase()}</button>
          ))}
        </div>
        <textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Log a quick note, call summary, or email recap…"
          className="w-full text-sm rounded-md border border-slate-300 bg-white px-3 py-2"
          data-testid="crm-activity-body"
        />
        <div className="flex justify-end mt-2">
          <Button size="sm" onClick={submit} disabled={busy || !body.trim()} className="bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="crm-activity-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
            Log activity
          </Button>
        </div>
      </div>
      {activities.length === 0 ? (
        <div className="text-sm text-slate-400 text-center py-6">No activity yet.</div>
      ) : (
        <ul className="space-y-3">
          {activities.map((a) => {
            const Icon = ACTIVITY_ICON[a.type] || StickyNote;
            return (
              <li key={a.id} className="flex gap-3" data-testid={`crm-activity-${a.id}`}>
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-50 ring-1 ring-cyan-200 flex items-center justify-center mt-0.5">
                  <Icon className="w-4 h-4 text-cyan-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-slate-800">{a.title || a.type}</span>
                    <span className="text-xs text-slate-400">· {formatRel(a.created_at)}</span>
                  </div>
                  {a.body && <div className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap">{a.body}</div>}
                  {a.created_by && <div className="text-xs text-slate-400 mt-0.5">by {a.created_by}</div>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function NotesPanel({ contact }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <h3 className="text-sm font-semibold text-slate-800 mb-2">Summary</h3>
      <div className="text-sm text-slate-700 whitespace-pre-wrap">{contact.notes_summary || <span className="text-slate-400">No summary saved yet. Use Edit to add a snapshot of this lead.</span>}</div>
    </div>
  );
}

function TasksPanel({ contactId, tasks, reload }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/crm/tasks`, {
        title: title.trim(),
        contact_id: contactId,
        due_date: due ? new Date(due).toISOString() : null,
        priority: "normal",
      });
      setTitle(""); setDue(""); setAdding(false);
      toast.success("Task added");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggle = async (task) => {
    try {
      await axios.put(`${API}/admin/crm/tasks/${task.id}`, { completed: !task.completed });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Tasks</h3>
        <Button variant="outline" size="sm" onClick={() => setAdding(!adding)} data-testid="crm-task-add-toggle">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {adding && (
        <div className="bg-slate-50 rounded-xl p-3 mb-3 space-y-2" data-testid="crm-task-add-form">
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task title…" className="w-full text-sm rounded-md border border-slate-300 px-3 py-2" data-testid="crm-task-title" />
          <input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} className="w-full text-sm rounded-md border border-slate-300 px-3 py-2" data-testid="crm-task-due" />
          <Button size="sm" onClick={submit} disabled={busy || !title.trim()} className="w-full bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="crm-task-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Add task
          </Button>
        </div>
      )}
      {tasks.length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-3">No tasks for this contact.</div>
      ) : (
        <ul className="space-y-2">
          {tasks.map((t) => (
            <li key={t.id} className="flex items-start gap-2 text-sm" data-testid={`crm-task-${t.id}`}>
              <button onClick={() => toggle(t)} className="mt-0.5" data-testid={`crm-task-toggle-${t.id}`}>
                {t.completed ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-slate-400" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`${t.completed ? "line-through text-slate-400" : "text-slate-800"} truncate`}>{t.title}</div>
                {t.due_date && <div className="text-xs text-slate-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(t.due_date).toLocaleString()}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RelationshipPanel({ contact }) {
  const overdueFollowUp = contact.next_follow_up_at && new Date(contact.next_follow_up_at) < new Date();
  return (
    <div className="bg-gradient-to-br from-indigo-50 to-cyan-50 border border-indigo-200 rounded-2xl p-5">
      <h3 className="text-sm font-semibold text-slate-800 mb-3">Relationship signals</h3>
      <ul className="text-xs text-slate-700 space-y-2">
        <li className="flex items-start gap-2">
          <Target className="w-3.5 h-3.5 mt-0.5 text-indigo-600" />
          <span>Lead score <strong>{contact.lead_score ?? 0}/100</strong> · priority <strong>{contact.priority_score ?? 0}/100</strong></span>
        </li>
        {contact.last_engagement_at && (
          <li className="flex items-start gap-2">
            <Sparkles className="w-3.5 h-3.5 mt-0.5 text-emerald-600" />
            <span>
              Last engagement: <strong>{contact.last_engagement_type || "—"}</strong>{" "}
              <span className="text-slate-500">· {formatRel(contact.last_engagement_at)}</span>
            </span>
          </li>
        )}
        {overdueFollowUp && (
          <li className="flex items-start gap-2 text-amber-700">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
            <span>Follow-up was due {formatRel(contact.next_follow_up_at)}</span>
          </li>
        )}
        {contact.last_contacted_at && (
          <li className="flex items-start gap-2">
            <Clock className="w-3.5 h-3.5 mt-0.5 text-slate-500" />
            <span>Last touch {formatRel(contact.last_contacted_at)}</span>
          </li>
        )}
        {contact.source && <li className="flex items-start gap-2"><Globe className="w-3.5 h-3.5 mt-0.5 text-slate-500" /><span className="break-all">Source: {contact.source}</span></li>}
        {contact.tags?.length > 0 && (
          <li className="flex items-start gap-2">
            <FileSignature className="w-3.5 h-3.5 mt-0.5 text-slate-500" />
            <span>Tags: {contact.tags.join(", ")}</span>
          </li>
        )}
      </ul>
      <div className="mt-4 text-[11px] text-slate-500 leading-relaxed">
        Use the <strong>AI Outreach</strong> button in the header to draft a message tailored to this contact's category and stage. Lead score auto-bumps when emails get delivered/opened/clicked.
      </div>
    </div>
  );
}
